import { CheckboxCustomEvent, IonSpinner, useIonToast } from '@ionic/react';
import { Business, FullPost, Post, objectEquals } from 'model';
import { useHistory, useParams } from 'react-router';
import { useGetBusinessQuery } from '../api/BusinessApi';
import {
	useDeletePostMutation,
	useGetPostQuery,
	useUpdateDisplayCommentsMutation,
	useUpdatePostMutation
} from '../api/PostApi';
import { BigImageContainer } from '../components/BigImageContainer';
import { BusinessHeader } from '../components/BusinessHeader';
import FormFooter from '../components/FormFooter';
import { Checkbox } from '../components/Forms/Checkbox';
import { FormInput } from '../components/Forms/FormInput';
import { FormTextarea } from '../components/Forms/FormTextarea';
import { TopicSearchBox } from '../components/Forms/TopicSearchBox';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { UploadImageButton } from '../components/UploadImageButton';
import { PostEditRules } from '../formConfigs/EditPostFieldsConfig';
import { useFormValues } from '../hooks/useFormValues';
import { usePasteURL } from '../hooks/usePasteURL';
import { useLoading } from '../hooks/useQuirkLoading';
import { isMutationError } from '../util/error';
import styles from './EditBusinessPost.module.css';
import { RealUserOnly } from './admin/RealUserOnly';

interface EditBusinessPostPageProps {
	business: Business;
	post: FullPost;
}

const EditBusinessPostPage: React.FC<EditBusinessPostPageProps> = ({ business, post }) => {
	const history = useHistory();
	const { setValues, formSuccess, formValues, validateForm, stateDataObject } = useFormValues(
		PostEditRules,
		post,
		objectEquals
	);
	const [deletePost, { isLoading: isDeleting }] = useDeletePostMutation();
	const [updateDisplayComments, { isLoading: isUpdatingDisplayComments }] = useUpdateDisplayCommentsMutation();
	const [updatePost, { isLoading: isUpdating }] = useUpdatePostMutation();
	const [showToast] = useIonToast();
	const onCancel = async () => {
		if (post.draft === true) {
			await deletePost(post._id);
		}
		history.goBack();
	};
	const onSubmit = async () => {
		if (validateForm()) {
			const result = await updatePost({
				post: {
					...post,
					...stateDataObject,
					draft: false
				}
			});
			if (isMutationError(result)) {
				showToast({
					duration: 1500,
					color: 'warning',
					position: 'top',
					message: 'There was an error updating the post'
				});
			} else {
				history.goBack();
				showToast({
					duration: 1500,
					color: 'success',
					position: 'top',
					message: isCheckIn ? `Checked in to ${business.name}` : 'Post has been updated',
					buttons: [
						{
							text: 'view',
							handler: () => {
								history.push(`/main/latest/post/show/${post._id}`);
							}
						}
					]
				});
			}
		}
	};
	const onCommentChange = async (e: CheckboxCustomEvent) => {
		await updateDisplayComments({ postid: post._id, display: e.target.checked });
	};
	const isCheckIn = post.postType === 'businessCheckIn';

	const { onPaste, loading: loadingPreview } = usePasteURL<FullPost>(setValues, 'contents');
	return (
		<QuirkIonPage
			name={isCheckIn ? `Business Check In` : 'Edit Business Post'}
			data={{ business: business._id, post: post._id }}
			roles={['user']}
		>
			<Header
				title={isCheckIn ? `Check in to ${business.name}` : 'Edit Post'}
				left={<IconButton icon={<IconClose />} onClick={onCancel} />}
				right={
					<IconButton
						fill="solid"
						label="Done"
						icon={<IconCheck />}
						onClick={onSubmit}
						disabled={isUpdating || !formSuccess}
					/>
				}
			/>
			<QuirkIonContent flush>
				<>
					<BusinessHeader business={business} />
					<div className={styles.editBusinessPostContent}>
						{isCheckIn && <div className={styles.checkInTitle}>{`Check in to ${business.name}`}</div>}
						{!isCheckIn && (
							<>
								<FormInput id="title" setValues={setValues} field={formValues?.title} label="Title" />
								<TopicSearchBox
									label="Topic"
									topic={formValues?.topic?.value ?? ''}
									setValues={setValues}
									errors={formValues?.topic?.errors}
								/>
							</>
						)}
						<div>
							<FormTextarea
								id="contents"
								nogap={post.images.length < 1}
								rows={7}
								setValues={setValues}
								field={formValues?.contents}
								label={isCheckIn ? 'Check in' : 'Content'}
								placeholder="Type your post, paste images or links to embed them"
								onPaste={onPaste}
							/>
							{loadingPreview && (
								<div className={styles.previewLoading}>
									<IonSpinner />
									<span>Loading Preview&hellip;</span>
								</div>
							)}
						</div>
						{post.images.length > 0 && (
							<div>
								<BigImageContainer post={post} edit={true} />
							</div>
						)}
						<div className={styles.postFooter}>
							<Checkbox
								onClick={onCommentChange}
								value={post.displayComments}
								label="Show Comments"
								disabled={isUpdatingDisplayComments || isDeleting}
							/>
							{post.images.length < 6 && <UploadImageButton post={post} iconRight />}
						</div>
						<FormFooter onCancel={onCancel} onSubmit={onSubmit} disableSubmit={!formSuccess} />
					</div>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};

export const EditBusinessPost: React.FC = () => {
	const { postid, businessid } = useParams<{ postid: Post['_id']; businessid: Business['_id'] }>();
	const { data: business, isLoading: isBusinessLoading } = useGetBusinessQuery(businessid);
	const { data: post, isLoading: isPostLoading } = useGetPostQuery(postid);
	const isLoading = isBusinessLoading || isPostLoading;
	useLoading({ isLoading });
	return <RealUserOnly>{business && post && <EditBusinessPostPage business={business} post={post} />}</RealUserOnly>;
};
